// client/src/pages/EditNotification.jsx
import React, { useState, useEffect } from 'react';
import DateTimePicker from 'react-datetime-picker';
import api from '../utils/api';
import { useNavigate, useParams } from 'react-router-dom';

// Normalize API datetime strings to a Date. We treat values without an offset as UTC.
function toDateFromApi(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  // If it already has a timezone offset or Z, parse directly
  if (/Z$|[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  // Convert "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ssZ" (UTC)
  s = s.replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
  return new Date(s);
}

export default function EditNotification() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [phones, setPhones] = useState([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [scheduledTime, setScheduledTime] = useState(null);
  const [error, setError] = useState('');


  // On mount: fetch the single notification, bank accounts, and confirmed phones
  useEffect(() => {
    // 1) Fetch notification details
    api
      .get(`/api/notifications/${id}`)
      .then((res) => {
        const notif = res.data.notification;
        setTitle(notif.title);
        setSelectedBank(notif.bank_account_id);
        setSelectedPhone(notif.phone_id);

        // Handle both start_datetime (recurring) and scheduled_time (one-time)
        const rawDate = notif.start_datetime || notif.scheduled_time;
        const parsedDate = toDateFromApi(rawDate);

        // Only set if we got a valid date
        if (parsedDate && !isNaN(parsedDate.getTime())) {
          setScheduledTime(parsedDate);
        } else {
          console.warn('Could not parse date from notification:', rawDate);
          setScheduledTime(null);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch notification:', err);
        setError('Could not load notification details.');
      });

    // 2) Fetch bank accounts
    api
      .get('/api/plaid/accounts')
      .then((res) => {
        setBankAccounts(res.data.accounts);
      })
      .catch((err) => {
        console.error('Failed to fetch bank accounts:', err);
        setError('Could not load bank accounts.');
      });

    // 3) Fetch confirmed phones
    api
      .get('/api/sms/phones')
      .then((res) => {
        setPhones(res.data.phones);
      })
      .catch((err) => {
        console.error('Failed to fetch phones:', err);
        setError('Could not load confirmed phones.');
      });
  }, [id, navigate]);

  const handleUpdate = async () => {
    setError('');
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!selectedBank) {
      setError('Please select a bank account.');
      return;
    }
    if (!selectedPhone) {
      setError('Please select a phone number.');
      return;
    }
    if (!scheduledTime) {
      setError('Please pick a date and time.');
      return;
    }

    try {
      const payload = {
        title,
        bankAccountId: selectedBank,
        phoneId: selectedPhone,
        scheduledTime: scheduledTime.toISOString(),
      };
      await api.put(`/api/notifications/${id}`, payload);
      navigate('/notifications');
    } catch (err) {
      console.error('Error updating notification:', err);
      const msg = err.response?.data?.error || 'Failed to update notification.';
      setError(msg);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Edit Notification</h1>

      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 space-y-4">
        <div>
          <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
            Notification Title
          </label>
          <input
            type="text"
            placeholder="Notification Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
            Select Bank Account
          </label>
          <select
            value={selectedBank}
            onChange={(e) => setSelectedBank(e.target.value)}
            className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">-- Choose Bank Account --</option>
            {bankAccounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.name} (****{acc.mask})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
            Select Phone Number
          </label>
          <select
            value={selectedPhone}
            onChange={(e) => setSelectedPhone(e.target.value)}
            className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">-- Choose Phone Number --</option>
            {phones.map((p) => (
              <option key={p.id} value={p.id}>
                {p.phone_name} ({p.phone_number})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
            Scheduled Time
          </label>
          <DateTimePicker
            onChange={setScheduledTime}
            value={scheduledTime}
            clearIcon={null}
            calendarIcon={null}
            className="w-full"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            className="w-full sm:w-auto px-6 py-3 min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            onClick={handleUpdate}
          >
            Save Changes
          </button>
          <button
            className="w-full sm:w-auto px-6 py-3 min-h-[44px] bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            onClick={() => navigate('/notifications')}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}